import styled from "@emotion/styled/macro";
import {
  Button,
  Chart,
  Tabs,
  tokens,
  Typography,
  SvgIcon,
  SvgNames,
  MOBILE_BREAKPOINT,
} from "@sunrun/experience-ui-components";
import { ReactNode, useEffect, useState } from "react";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { formatCurrency } from "../../utils/formatHelpers";
import priceOfPowerIllustration from "assets/images/price-of-power.svg";
import {
  PriceOfPowerProspect,
  usePriceOfPowerUrl,
} from "hooks/usePriceOfPowerUrl";
import { maxWidth } from "components/atoms/GlobalStyles";
import {
  formatDataForMonthly,
  monthsAndDays,
  getMonthProperty,
} from "utils/usageUtils";
import {
  financialProductDisplayText,
  FLEX_MONTHLY,
} from "constants/financialProducts";

import { addSelfSupplyToolTips, addToolTips } from "utils/graphUtils";
import {
  getSelfSupplyMonthlySeries,
  hasMonthlySelfSupplyData,
} from "utils/selfSupplyChartData";
import { SelfSupplyTooltipFlyout } from "components/organisms/SelfSupplyTooltipFlyout";

const SELF_SUPPLY_ENERGY_LABEL = "Est. energy directly from the Sunrun system";
const UTILITY_REMAINING_ENERGY_LABEL = "Est. energy remaining from the utility";
const OFFER_REVIEW_MONTHLY_BAR_RATIO = 0.6;

type Props = {
  offer: ReviewOffer;
  prospect?: PriceOfPowerProspect;
};

type ContainerProps = Props & {
  isMobile?: boolean;
  showProduction?: boolean;
  showTarget?: boolean;
  showFuture?: boolean;
  showSelfSupply?: boolean;
  graphTabOrder?: "default" | "targetZoneFirst";
  energyProfileSelectNonce?: number;
  testId?: string;
};

const ENERGY_PROFILE_TAB_LABEL = "Energy profile";

const getContent = (
  isFlexMonthly: boolean,
  offer?: ReviewOffer
): {
  /** the array that defines the graphs legend content */
  legendInfo: { name: string; symbol: { type?: string; fill?: string } }[];
  /** the heading or title next to the graph */
  graphHeading: ReactNode;
  /** the description under the graph heading */
  graphDescription: string;
  /** the white dotted line data */
  areaData: { x: number; y: number; y0: 0 }[];
  /** the blue dotted line data */
  secondaryAreaData: { x: number; y: number; y0: 0 }[];
} => {
  let legendInfo: {
    name: string;
    symbol: { type?: string; fill?: string };
    offset?: { x: number; y: number };
    height?: number;
    width?: number;
  }[];
  let graphHeading: ReactNode;
  let graphDescription: string;
  let areaData: { x: number; y: number; y0: 0 }[];
  let secondaryAreaData: { x: number; y: number; y0: 0 }[];

  if (isFlexMonthly) {
    legendInfo = [
      {
        name: "Usage below solar production",
        symbol: { fill: tokens.BRAND_CHARGEBLUE, type: SvgNames.LegendBar },
        height: 15,
        width: 15,
        offset: { x: -5, y: -5 },
      },
      {
        name: "Monthly energy baseline",
        symbol: { type: SvgNames.LegendLine },
        offset: { x: -1, y: -7 },
      },
      {
        name: "Usage above solar production",
        symbol: { fill: tokens.INFO_60, type: SvgNames.LegendBar },
        height: 15,
        width: 15,
        offset: { x: -5, y: -5 },
      },
      {
        name: `${financialProductDisplayText[FLEX_MONTHLY]} production`,
        symbol: { type: SvgNames.LegendDots },
        offset: { x: -1, y: -7 },
      },
      {
        name: "Solar production",
        symbol: {
          fill: tokens.TINTS_CHARGE_BLUE_30,
          type: SvgNames.LegendLine,
        },
        offset: { x: 1, y: -7 },
      },
    ];
    graphHeading = (
      <>
        You're
        <br />
        unlocking
        <br />
        energy for
        <br />
        life
      </>
    );
    graphDescription = `Your new energy system is designed to create solar energy with the industry's best products. And with ${financialProductDisplayText[FLEX_MONTHLY]}, you are all set for your future increased electricity needs.`;
    areaData = [
      offer?.monthlyProductionBaseline?.jan ?? 0,
      offer?.monthlyProductionBaseline?.feb ?? 0,
      offer?.monthlyProductionBaseline?.mar ?? 0,
      offer?.monthlyProductionBaseline?.apr ?? 0,
      offer?.monthlyProductionBaseline?.may ?? 0,
      offer?.monthlyProductionBaseline?.jun ?? 0,
      offer?.monthlyProductionBaseline?.jul ?? 0,
      offer?.monthlyProductionBaseline?.aug ?? 0,
      offer?.monthlyProductionBaseline?.sep ?? 0,
      offer?.monthlyProductionBaseline?.oct ?? 0,
      offer?.monthlyProductionBaseline?.nov ?? 0,
      offer?.monthlyProductionBaseline?.dec ?? 0,
    ].map((value, index) => ({ x: index + 1, y: value, y0: 0 }));
    secondaryAreaData = [
      offer?.monthlyProduction.jan ?? 0,
      offer?.monthlyProduction.feb ?? 0,
      offer?.monthlyProduction.mar ?? 0,
      offer?.monthlyProduction.apr ?? 0,
      offer?.monthlyProduction.may ?? 0,
      offer?.monthlyProduction.jun ?? 0,
      offer?.monthlyProduction.jul ?? 0,
      offer?.monthlyProduction.aug ?? 0,
      offer?.monthlyProduction.sep ?? 0,
      offer?.monthlyProduction.oct ?? 0,
      offer?.monthlyProduction.nov ?? 0,
      offer?.monthlyProduction.dec ?? 0,
    ].map((value, index) => ({ x: index + 1, y: value, y0: 0 }));
  } else {
    legendInfo = [
      {
        name: "Usage below solar production",
        symbol: { fill: tokens.BRAND_CHARGEBLUE, type: SvgNames.LegendBar },
        height: 15,
        width: 15,
        offset: { x: -5, y: -5 },
      },
      {
        name: "Usage above solar production",
        symbol: { fill: tokens.INFO_60, type: SvgNames.LegendBar },
        height: 15,
        width: 15,
        offset: { x: -5, y: -5 },
      },
      {
        name: "Solar production",
        symbol: { type: SvgNames.LegendLine },
        offset: { x: 1, y: -7 },
      },
    ];
    graphHeading = "Your home, Your energy";
    graphDescription =
      "By generating your own affordable energy, you can power your home, take control over your energy bills, and increase energy independence.";
    areaData = [
      offer?.monthlyProduction.jan ?? 0,
      offer?.monthlyProduction.feb ?? 0,
      offer?.monthlyProduction.mar ?? 0,
      offer?.monthlyProduction.apr ?? 0,
      offer?.monthlyProduction.may ?? 0,
      offer?.monthlyProduction.jun ?? 0,
      offer?.monthlyProduction.jul ?? 0,
      offer?.monthlyProduction.aug ?? 0,
      offer?.monthlyProduction.sep ?? 0,
      offer?.monthlyProduction.oct ?? 0,
      offer?.monthlyProduction.nov ?? 0,
      offer?.monthlyProduction.dec ?? 0,
    ].map((value, index) => ({ x: index + 1, y: value, y0: 0 }));
    secondaryAreaData = [];
  }
  return {
    legendInfo,
    graphHeading,
    graphDescription,
    areaData,
    secondaryAreaData,
  };
};

const getMonthlyEnergyBaselineLineData = (offer?: ReviewOffer) => {
  const monthlyEnergyBaseline = (offer?.consumption ?? 0) / 12;
  return Array.from({ length: 12 }, (_, index) => ({
    x: index + 1,
    y: monthlyEnergyBaseline,
    y0: 0 as const,
  }));
};

const SolarProduction = ({ offer, isMobile }: ContainerProps) => {
  const isFlexMonthly = offer?.financial?.summary?.type === "FlexMonthly";

  const usage = getMonthProperty(offer?.usage);

  const production = getMonthProperty(offer?.monthlyProduction);

  const baselineProduction = isFlexMonthly
    ? Array(12).fill((offer?.consumption ?? 0) / 12)
    : getMonthProperty(offer?.monthlyProductionBaseline);

  const monthlyData = formatDataForMonthly(usage, monthsAndDays, "monthly");

  const barData = addToolTips({
    barData: monthlyData,
    production,
    isFlexMonthly,
    baselineProduction,
    useMonthlyEnergyBaseline: isFlexMonthly,
  });

  const getDomainSize = () => window.innerWidth / 100 + 16;
  const content = getContent(isFlexMonthly, offer);
  const lineData = isFlexMonthly
    ? getMonthlyEnergyBaselineLineData(offer)
    : content.areaData;
  return (
    <NewContainer>
      <SolarTextContainer>
        <Typography
          size={tokens.FONT_SIZE_8}
          color={tokens.BRAND_HEROBLUE}
          lineHeight={53}
        >
          {content.graphHeading}
        </Typography>
        <br />
        <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
          {content.graphDescription}
        </Typography>
      </SolarTextContainer>
      <SolarCard>
        <Chart
          xLabel="month"
          headerValue="Estimated monthly values"
          barData={barData}
          lineData={lineData}
          secondaryLineData={content.secondaryAreaData}
          tertiaryLineData={isFlexMonthly ? content.areaData : []}
          colors={
            isFlexMonthly
              ? { tertiaryDataLine: tokens.TINTS_CHARGE_BLUE_30 }
              : undefined
          }
          showXGrid
          howManyXAxis={4}
          legendData={content.legendInfo}
          barRatio={OFFER_REVIEW_MONTHLY_BAR_RATIO}
          toolTipAlignment={-125}
          toolTipFontSize={18}
          useToolTip={true}
          domainPadding={getDomainSize()}
          excessGraph={true}
          lineStrokeWidth={4}
          legendFontSize={12}
          legendOnTop={false}
          legendOffsetY={isFlexMonthly ? -70 : -10}
          legendOffsetX={10}
          legendItemsPerRow={isFlexMonthly ? 2 : 0}
          legendBreakPoint={isFlexMonthly ? 390 : 550}
          mobileLegend={{
            itemsPerRow: 1,
            padding: isFlexMonthly ? 100 : 60,
            offsetY: isFlexMonthly ? -100 : -60,
          }}
          headerOffsetX={20}
          showYGrid={false}
          addedBottomPadding={isFlexMonthly ? 80 : 0}
          toolTipLabelProps={{
            dx: -125,
            style: [
              {
                fontWeight: "500",
                fontFamily: "Roobert, sans-serif",
                fill: tokens.HEROBLUE_20,
                fontSize: "20px",
              },
              ...new Array(4).fill({
                fontWeight: "300",
                fontFamily: "Roobert, sans-serif",
                fill: tokens.HEROBLUE_20,
                fontSize: "16px",
              }),
            ],
          }}
          toolTipPointerLength={20}
          toolTipPointerWidth={40}
          toolTipProps={{
            constrainToVisibleArea: false,
            flyoutStyle: {
              strokeWidth: 30,
              stroke: tokens.TINTS_INFO_80,
              fill: tokens.TINTS_INFO_80,
              minWidth: 300,
            },
          }}
        />
      </SolarCard>
    </NewContainer>
  );
};

const TargetZone = ({ offer }: ContainerProps) => {
  const isFlexMonthly = offer?.financial?.summary?.type === "FlexMonthly";

  const usage = getMonthProperty(offer?.usage);

  const production = getMonthProperty(offer?.monthlyProduction);

  const baselineProduction = getMonthProperty(offer?.monthlyProductionBaseline);

  const monthlyData = formatDataForMonthly(usage, monthsAndDays, "monthly");

  const barData = addToolTips({
    barData: monthlyData,
    production,
    isFlexMonthly,
    baselineProduction,
  });

  const getDomainSize = () => window.innerWidth / 100 + 16;
  const areaData = getContent(isFlexMonthly, offer).areaData;
  const targetDifference =
    (areaData.reduce((accumulator, { y }) => accumulator + y, 0) * 0.2) / 12;

  return (
    <NewContainer>
      <SolarTextContainer>
        <Typography
          size={tokens.FONT_SIZE_8}
          color={tokens.BRAND_HEROBLUE}
          lineHeight={53}
        >
          Your solar production on target
        </Typography>
        <br />
        <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
          It's normal for solar production to vary from month to month due to
          changes in weather. The Target Zone in this graph shows the normal
          range of solar production for your system based on its design, local
          peer data, and seasonality of solar production.
        </Typography>
      </SolarTextContainer>
      <SolarCard>
        <Chart
          xLabel="month"
          barData={barData}
          areaData={areaData.map(({ x, y }) => ({
            x,
            y,
            y0: y - targetDifference,
          }))}
          lineData={areaData.map(({ x, y }) => ({
            x,
            y: y - targetDifference,
          }))}
          xLabelFormat={(label) => label?.toString().charAt(0)}
          showXGrid
          howManyXAxis={4}
          legendData={[
            {
              name: "Target Zone",
              symbol: {
                type: "minus",
                stroke: tokens.TINTS_SUCCESS_70,
                strokeWidth: 1,
                fillOpacity: 0.5,
                fill: tokens.TINTS_SUCCESS_70,
              },
            },
            {
              name: "Est. Energy Usage",
              symbol: { fill: tokens.BRAND_CHARGEBLUE },
            },
          ]}
          barRatio={OFFER_REVIEW_MONTHLY_BAR_RATIO}
          toolTipAlignment={-125}
          toolTipFontSize={18}
          useToolTip={true}
          domainPadding={getDomainSize()}
          toolTipLabelProps={{
            dx: -125,
            style: [
              {
                fontWeight: "500",
                fontFamily: "Roobert, sans-serif",
                fill: tokens.HEROBLUE_20,
                fontSize: "20px",
              },
              ...new Array(4).fill({
                fontWeight: "300",
                fontFamily: "Roobert, sans-serif",
                fill: tokens.HEROBLUE_20,
                fontSize: "16px",
              }),
            ],
          }}
          toolTipPointerLength={20}
          toolTipPointerWidth={40}
          toolTipProps={{
            constrainToVisibleArea: false,
            flyoutStyle: {
              strokeWidth: 30,
              stroke: tokens.TINTS_INFO_80,
              fill: tokens.TINTS_INFO_80,
              minWidth: 300,
            },
          }}
          styles={{
            area: {
              data: {
                stroke: tokens.TINTS_SUCCESS_70,
                strokeWidth: 1,
                fillOpacity: 0.5,
                fill: tokens.TINTS_SUCCESS_70,
              },
            },
            primaryLine: {
              data: {
                stroke: tokens.TINTS_SUCCESS_70,
                strokeWidth: 1,
              },
            },
          }}
          primaryLineLabels={[""]}
        />
      </SolarCard>
    </NewContainer>
  );
};

const SunrunFutureBills = ({ offer }: ContainerProps) => {
  const xLabelFormatter = (label: any) => {
    return "Year " + label.toString().slice(0, 2);
  };

  const yTickFormatter = (tick: number) => {
    return formatCurrency(tick);
  };

  let maxSavingsData;
  let minSavingsData;
  let annualCumulativeCostWithoutSolar: Array<{ min: number; max: number }>;

  const totalAnnualCumCostWOSolar = () => {
    if (offer.financial.summary.future25YearPredictionData) {
      annualCumulativeCostWithoutSolar = {
        ...offer.financial.summary.future25YearPredictionData
          .annualCumulativeCostWithoutSolar,
      };

      maxSavingsData = [
        { x: 0, y: annualCumulativeCostWithoutSolar[0].max },
        { x: 5, y: annualCumulativeCostWithoutSolar[4].max },
        { x: 10, y: annualCumulativeCostWithoutSolar[9].max },
        { x: 15, y: annualCumulativeCostWithoutSolar[14].max },
        { x: 20, y: annualCumulativeCostWithoutSolar[19].max },
        { x: 25, y: annualCumulativeCostWithoutSolar[24].max },
      ];

      minSavingsData = [
        { x: 0, y: annualCumulativeCostWithoutSolar[0].min },
        { x: 5, y: annualCumulativeCostWithoutSolar[4].min },
        { x: 10, y: annualCumulativeCostWithoutSolar[9].min },
        { x: 15, y: annualCumulativeCostWithoutSolar[14].min },
        { x: 20, y: annualCumulativeCostWithoutSolar[19].min },
        { x: 25, y: annualCumulativeCostWithoutSolar[24].min },
      ];
      return annualCumulativeCostWithoutSolar;
    } else return null;
  };

  const totalAnnualCumCostResult = totalAnnualCumCostWOSolar();
  const totalSavings = totalAnnualCumCostResult
    ? {
        min: totalAnnualCumCostResult[24].min,
        max: totalAnnualCumCostResult[24].max,
      }
    : null;

  return minSavingsData && maxSavingsData ? (
    <NewContainer>
      <SolarTextContainer>
        <Typography size={tokens.FONT_SIZE_8} color={tokens.BRAND_HEROBLUE}>
          Your cost of doing nothing
        </Typography>
        <br />
        <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
          Without solar you could be paying&nbsp;{" "}
          {formatCurrency(totalSavings?.min)} - &nbsp;
          {formatCurrency(totalSavings?.max)} for electricity over 25 years.
        </Typography>
        <br />
        <Typography size={"extra_small"} color={tokens.BRAND_HEROBLUE}>
          *These predictions are based on historical rates from your utility,
          data from entire populations across your ZIP code, and trends in
          electricity. This is an estimate of utility costs only and does not
          represent a guarantee of savings. You will still have a bill with your
          utility company. The amount you owe your utility depends on utility
          rate schedules, fixed charges, seasonal changes in solar output, and
          if you use more power than you did in the past.
        </Typography>
      </SolarTextContainer>
      <SolarCard>
        <Chart
          xLabel="annual"
          lineData={maxSavingsData}
          primaryLineLabels={[]}
          secondaryLineData={minSavingsData}
          secondaryLineLabels={[]}
          yTickFormat={yTickFormatter}
          xLabelFormat={xLabelFormatter}
          showXGrid
          howManyXAxis={5}
          legendData={[
            {
              name: "High",
              symbol: { type: "minus" },
            },
            {
              name: "Low",
              symbol: { fill: tokens.BRAND_CHARGEBLUE },
            },
          ]}
        />
      </SolarCard>
    </NewContainer>
  ) : (
    <div>
      Sorry, our crystal ball is having trouble seeing into this future. Please
      contact Sales Dev support - Ryne Gross
    </div>
  );
};

const SelfSupply = ({ offer }: ContainerProps) => {
  const monthlySeries = getSelfSupplyMonthlySeries(offer);

  if (!monthlySeries) {
    return null;
  }

  const monthlyData = formatDataForMonthly(
    monthlySeries.usage,
    monthsAndDays,
    "monthly"
  );

  const barData = addSelfSupplyToolTips({
    barData: monthlyData,
    selfSupply: monthlySeries.selfSupply,
    utilityImport: monthlySeries.utilityImport,
  });

  const selfSupplySplitLineData = monthlySeries.selfSupply.map(
    (value, index) => ({
      x: index + 1,
      y: value,
      y0: 0 as const,
    })
  );

  const selfSupplyLegendData = [
    {
      name: SELF_SUPPLY_ENERGY_LABEL,
      symbol: { fill: tokens.BRAND_CHARGEBLUE, type: SvgNames.LegendBar },
      height: 15,
      width: 15,
      offset: { x: -5, y: -5 },
    },
    {
      name: UTILITY_REMAINING_ENERGY_LABEL,
      symbol: { fill: tokens.INFO_60, type: SvgNames.LegendBar },
      height: 15,
      width: 15,
      offset: { x: -5, y: -5 },
    },
  ];

  const getDomainSize = () => window.innerWidth / 100 + 16;

  return (
    <NewContainer>
      <SolarTextContainer>
        <Typography
          size={tokens.FONT_SIZE_8}
          color={tokens.BRAND_HEROBLUE}
          lineHeight={44}
        >
          Reduce your utility dependence
        </Typography>
        <br />
        <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
          Self supply measures how much of your energy will be consumed directly
          from the Sunrun system. We've designed it to reduce the amount of
          energy you'll need from the utility!
        </Typography>
      </SolarTextContainer>
      <SelfSupplyCard>
        <ChartWrapper>
          <Chart
            xLabel="month"
            headerValue="Estimated monthly values"
            barData={barData}
            secondaryLineData={selfSupplySplitLineData}
            showXGrid
            howManyXAxis={4}
            legendData={selfSupplyLegendData}
            barRatio={OFFER_REVIEW_MONTHLY_BAR_RATIO}
            useToolTip={true}
            toolTipProps={{
              flyoutComponent: <SelfSupplyTooltipFlyout />,
              flyoutStyle: { fill: "transparent", strokeWidth: 0 },
            }}
            toolTipLabelProps={{
              style: [{ fill: "transparent", fontSize: "0px" }],
            }}
            domainPadding={getDomainSize()}
            excessGraph={true}
            lineStrokeWidth={4}
            legendFontSize={12}
            legendOnTop={false}
            legendOffsetY={-10}
            legendOffsetX={10}
            legendBreakPoint={550}
            mobileLegend={{
              itemsPerRow: 1,
              padding: 80,
              offsetY: -80,
            }}
            headerOffsetX={20}
            showYGrid={false}
            addedBottomPadding={20}
            styles={{
              secondaryLine: {
                data: { stroke: "transparent", strokeWidth: 0 },
              },
            }}
          />
        </ChartWrapper>
      </SelfSupplyCard>
    </NewContainer>
  );
};

const PriceOfPowerButton = styled(Button)`
  margin-top: 24px;
  align-self: flex-start;
`;

const PriceOfPowerMedia = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: ${maxWidth}px) {
    margin-top: 35px;
  }
`;

const PriceOfPowerIllustration = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const PriceOfPower = ({
  prospect,
  preSolarUtility,
}: {
  prospect?: PriceOfPowerProspect;
  preSolarUtility?: number;
}) => {
  const priceOfPowerUrl = usePriceOfPowerUrl(prospect, preSolarUtility);

  const openPriceOfPower = () => {
    window.open(priceOfPowerUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <NewContainer>
      <SolarTextContainer>
        <Typography
          size={tokens.FONT_SIZE_8}
          color={tokens.BRAND_HEROBLUE}
          lineHeight={53}
        >
          Price of power
        </Typography>
        <br />
        <Typography size={tokens.FONT_SIZE_2} color={tokens.BRAND_HEROBLUE}>
          Explore past 5 year regional rate changes where you live
        </Typography>
        <PriceOfPowerButton
          color="primary"
          size="sm"
          onClick={openPriceOfPower}
          iconRight={
            <SvgIcon
              name={SvgNames.ExternalLink}
              width={16}
              height={16}
              color={tokens.WHITE}
              style={{ height: 16, width: 16 }}
            />
          }
        >
          Price of power
        </PriceOfPowerButton>
      </SolarTextContainer>
      <PriceOfPowerMedia>
        <PriceOfPowerIllustration
          src={priceOfPowerIllustration}
          alt="Price of power tool preview"
        />
      </PriceOfPowerMedia>
    </NewContainer>
  );
};

const GraphContent = ({
  offer,
  prospect,
  showProduction = true,
  showTarget = true,
  showFuture = true,
  showSelfSupply = false,
  graphTabOrder = "default",
  energyProfileSelectNonce = 0,
  testId,
}: ContainerProps) => {
  const showSelfSupplyTab = showSelfSupply && hasMonthlySelfSupplyData(offer);
  const isTargetZoneFirst = graphTabOrder === "targetZoneFirst";
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const targetZoneTab = showTarget ? ["Target Zone"] : [];
  const targetZoneContent = showTarget
    ? [<TargetZone key="target-zone" offer={offer} />]
    : [];

  const tabs = [
    ...(isTargetZoneFirst ? targetZoneTab : []),
    ...(showSelfSupplyTab ? [ENERGY_PROFILE_TAB_LABEL] : []),
    ...(showProduction ? ["Solar production"] : []),
    ...(isTargetZoneFirst ? [] : targetZoneTab),
    ...(showFuture ? ["Future Utility Costs"] : []),
    "Price of power",
  ];

  const contents = [
    ...(isTargetZoneFirst ? targetZoneContent : []),
    ...(showSelfSupplyTab
      ? [<SelfSupply key="self-supply" offer={offer} />]
      : []),
    ...(showProduction
      ? [<SolarProduction key="solar-production" offer={offer} />]
      : []),
    ...(isTargetZoneFirst ? [] : targetZoneContent),
    ...(showFuture
      ? [<SunrunFutureBills key="future-utility-costs" offer={offer} />]
      : []),
    <PriceOfPower
      prospect={prospect}
      preSolarUtility={offer?.financial?.summary?.preSolarUtility}
    />,
  ];

  const energyProfileTabIndex = tabs.indexOf(ENERGY_PROFILE_TAB_LABEL);

  useEffect(() => {
    if (energyProfileSelectNonce && energyProfileTabIndex >= 0) {
      setActiveTabIndex(energyProfileTabIndex);
    }
  }, [energyProfileSelectNonce, energyProfileTabIndex]);

  return (
    <NewGraphContentContainer data-testid={testId}>
      <Tabs
        tabs={tabs}
        contents={contents}
        initialTabIndex={activeTabIndex}
        onSelectTab={setActiveTabIndex}
      />
    </NewGraphContentContainer>
  );
};

const SolarTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  text-align: left;
  color: #1f2647;
  width: 263px;
  min-width: 263px;
  max-width: 263px;
  font-style: normal;

  @media (max-width: 900px) {
    width: 100%;
    min-width: unset;
    max-width: unset;
    padding: 0 10px;
    box-sizing: border-box;
  }
`;

const SolarCard = styled.div`
  height: 485px;
  width: auto;
  flex: 1;
  color: white;
  border: 1px solid #ffffff;
  box-sizing: border-box;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  background: linear-gradient(180deg, #1f2647 0%, #000000 270.13%);
  appearance: none;
  text-align: left;
  margin-left: 35px;

  @media (max-width: 900px) {
    margin-left: 0px;
    margin-top: 0px;
    flex: unset;
    height: 485px;
    width: auto;
  }

  @media (max-width: ${maxWidth}px) {
    margin-left: 0px;
    margin-top: 35px;
    flex: unset;
    height: 485px;
    width: auto;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid ${tokens.BRAND_MEDIUMBLUE};
  }
`;

const ChartWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

const SelfSupplyCard = styled.div`
  height: 620px;
  display: flex;
  flex-direction: column;
  width: auto;
  flex: 1;
  color: white;
  border: 1px solid ${tokens.WHITE};
  box-sizing: border-box;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  background: #090a10;
  appearance: none;
  text-align: left;
  margin-left: 35px;

  @media (max-width: 900px) {
    margin-left: 0px;
    margin-top: 0px;
    flex: unset;
    height: 580px;
    width: auto;
  }

  @media (max-width: ${maxWidth}px) {
    margin-left: 0px;
    margin-top: 35px;
    flex: unset;
    height: 580px;
    width: auto;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid ${tokens.BRAND_MEDIUMBLUE};
  }
`;

const NewGraphContentContainer = styled.div`
  margin-bottom: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    & div[role="tablist"] {
      box-sizing: border-box;
    }
  }
`;

const NewContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 48px;
  padding-top: 32px;

  @media (max-width: 900px) {
    flex-direction: column;
  }

  @media (max-width: ${maxWidth}px) {
    gap: 16px;
    padding-top: 32px;
  }

  & > div:last-of-type {
    margin: 0;
  }
`;

export type { ContainerProps as GraphContentProps, Props };
export { GraphContent };
